import { observable, action } from 'mobx';
import { dataorchApi } from './api';

// ID type options for bulk search
export const ID_TYPE_OPTIONS = [
  { value: 'tracking_code', label: 'Tracking Code' },
  { value: 'id', label: 'Shipment ID' },
  { value: 'internal_id', label: 'Internal ID' },
];

// Default limit for bulk search IDs
export const DEFAULT_BULK_SEARCH_LIMIT = 250;

class ShipmentSearchStore {
  @observable results = {};
  @observable searching = false;
  @observable query = null;
  @observable regions = [];
  @observable profiles = [];
  sorts = ["-dropoff_earliest_ts"]
  searchCriteria = {};
  pageSize = 10;

  // Bulk search state
  @observable isBulkMode = false;
  @observable bulkIdType = 'tracking_code';
  @observable bulkIdsText = '';
  @observable bulkLimitWarning = null;
  @observable bulkSearchIds = [];

  /**
   * Parse bulk IDs text input into an array of unique, trimmed IDs
   * Supports both comma-separated and one-per-line formats
   * Optimized for single-pass processing with early termination
   */
  @action
  parseBulkIds(text, limit = DEFAULT_BULK_SEARCH_LIMIT) {
    if (!text || !text.trim()) {
      return { ids: [], truncated: false, originalCount: 0 };
    }

    // Use regex to match non-empty sequences between delimiters
    // This avoids creating intermediate arrays from split/map/filter
    const idPattern = /[^,\n\r\s][^,\n\r]*[^,\n\r\s]|[^,\n\r\s]/g;
    const seen = new Set();
    const ids = [];
    let match;

    while ((match = idPattern.exec(text)) !== null) {
      const id = match[0].trim();
      if (id && !seen.has(id)) {
        seen.add(id);
        // Continue counting for originalCount even after limit reached
        if (ids.length < limit) {
          ids.push(id);
        }
      }
    }

    const originalCount = seen.size;
    const truncated = originalCount > limit;

    return { ids, truncated, originalCount };
  }

  /**
   * Get the bulk search limit from client settings or use default
   * Handles string, number, and invalid input types safely
   */
  getBulkSearchLimit(clientSettings) {
    const configLimit = clientSettings?.settings?.bulk_search_limit;

    if (configLimit == null) {
      return DEFAULT_BULK_SEARCH_LIMIT;
    }

    // Handle numeric values directly
    if (typeof configLimit === 'number' && Number.isFinite(configLimit) && configLimit > 0) {
      return Math.floor(configLimit);
    }

    // Handle string values - ensure non-empty string before parsing
    if (typeof configLimit === 'string' && configLimit.trim() !== '') {
      const parsed = parseInt(configLimit, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return DEFAULT_BULK_SEARCH_LIMIT;
  }

  processQuery(page) {
    let filters = {};
    if (!!this.regions && !!this.regions.length) {
      filters.region = this.regions.join(",");
    }
    if (!!this.profiles && !!this.profiles.length) {
      filters.client_profile_id = this.profiles.join(",");
    }

    let query = {
      from: page ? ((page - 1) * this.pageSize) : 0,
      size: this.pageSize,
      sorts: this.sorts,
      filters
    };

    // Only include 'q' parameter if there's a valid query string
    if (this.query != null && this.query !== '') {
      query.q = this.query;
    }

    if (this.statuses && this.statuses.length > 0) {
      query = Object.assign(query, {status: this.statuses});
    }

    return query;
  }

  processBulkQuery(page, ids) {
    let filters = {};
    if (!!this.regions && !!this.regions.length) {
      filters.region = this.regions.join(",");
    }
    if (!!this.profiles && !!this.profiles.length) {
      filters.client_profile_id = this.profiles.join(",");
    }

    // Add the bulk ID filter based on selected ID type
    filters[this.bulkIdType] = ids.join(",");

    let query = {
      from: page ? ((page - 1) * this.pageSize) : 0,
      size: this.pageSize,
      sorts: this.sorts,
      filters
    };

    if (this.statuses && this.statuses.length > 0) {
      query = Object.assign(query, {status: this.statuses});
    }

    return query;
  }

  @action
  setIsBulkMode(isBulk) {
    this.isBulkMode = isBulk;
  }

  @action
  setBulkIdType(idType) {
    this.bulkIdType = idType;
  }

  @action
  setBulkIdsText(text) {
    this.bulkIdsText = text;
  }

  @action
  setBulkLimitWarning(warning) {
    this.bulkLimitWarning = warning;
  }

  @action
  setBulkSearchIds(ids) {
    this.bulkSearchIds = ids;
  }

  @action
  clearSearch() {
    this.query = null;
    this.bulkIdsText = '';
    this.bulkLimitWarning = null;
    this.bulkSearchIds = [];
    this.results = {};
    this.searching = false;
  }

  refreshSearch(q) {
    this.query = q
    this.search(0)
  }

  search(page) {
    this.searching = true;

    dataorchApi.post('/shipments/search', this.processQuery(page))
      .then(response => {
        this.searching = false;
        if (response.ok && response.data && typeof response.data !== 'string') {
          this.results = response.data
          this.results.page = 1 + this.results.from / this.results.size
          this.results.total_pages = Math.ceil(this.results.total / this.results.size)
        } else {
          this.results = {
            total: 0,
            from: 0,
            size: this.pageSize,
            page: 0,
            total_pages: 0
          }
          console.error('Search API failed:', response.problem, response.data)
        }
      })
      .catch(error => {
        this.searching = false;
        this.results = {
          total: 0,
          from: 0,
          size: this.pageSize,
          page: 0,
          total_pages: 0
        }
        console.error('Search API exception:', error)
      })
  }

  @action
  bulkSearch(page, ids) {
    if (!ids || ids.length === 0) {
      this.results = {};
      return;
    }

    this.searching = true;

    dataorchApi.post('/shipments/search', this.processBulkQuery(page, ids))
      .then(response => {
        this.searching = false;
        if (response.ok && response.data && typeof response.data !== 'string') {
          this.results = response.data
          this.results.page = 1 + this.results.from / this.results.size
          this.results.total_pages = Math.ceil(this.results.total / this.results.size)
        } else {
          this.results = {
            total: 0,
            from: 0,
            size: this.pageSize,
            page: 0,
            total_pages: 0
          }
          console.error('Bulk search API failed:', response.problem, response.data)
        }
      })
      .catch(error => {
        this.searching = false;
        this.results = {
          total: 0,
          from: 0,
          size: this.pageSize,
          page: 0,
          total_pages: 0
        }
        console.error('Bulk search API exception:', error)
      })
  }

}

export default ShipmentSearchStore;
